import { LayoutDashboard, Phone, X } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { HEADER_CONFIG } from "@/src_new/shared/config/header.config";
import {
  getThemeClass,
  getThemeComponentClasses,
} from "@/src_new/shared/lib/themeComponentClasses";
import { usePosterReducers } from "@/src_new/shared/redux/store/useGetData";
import { App_Url_New } from "../common/static";

type SiteMobileNavProps = {
  open: boolean;
  onClose: () => void;
};

export function SiteMobileNav({ open, onClose }: SiteMobileNavProps) {
  const headerClasses = getThemeComponentClasses().header;
  const navClasses = getThemeComponentClasses().nav;
  const tableClasses = getThemeComponentClasses().table;
  const navigate = useNavigate();
  const location = useLocation();
  const {
    auth: { customerAuth },
  } = usePosterReducers();

  const accessToken = customerAuth?.access_token || null;
  const dashboardUrl = App_Url_New.retail.frontlinks.retail_dashboard;
  const isDashboardPage = location.pathname === dashboardUrl;
  const showDashboard = accessToken && !isDashboardPage;

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 bg-theme-overlay/80">
      <div
        className={`absolute right-0 top-0 flex h-full w-[320px] flex-col overflow-y-auto thin-scrollbar p-6 ${getThemeClass(headerClasses.bgColor, "bg-theme-accent")} ${getThemeClass(headerClasses.textColor, "text-theme-text")}`}
      >
        <button type="button" onClick={onClose} className="mb-6 self-start" aria-label="Close menu">
          <X
            className={`h-6 w-6 ${getThemeClass(
              headerClasses.menuIconColor || headerClasses.textColor,
              "text-theme-text"
            )}`}
          />
        </button>

        <div className="flex flex-col gap-6">
          {showDashboard ? (
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate(dashboardUrl);
              }}
              className={`flex w-fit items-center gap-2 rounded-full px-4 py-2 text-base font-semibold transition-all duration-200 ${getThemeClass(tableClasses.controlBgColor, "bg-theme-text")} ${getThemeClass(tableClasses.controlTextColor, "text-theme-text-inverse")} hover:bg-theme-text/85`}
            >
              <LayoutDashboard className="h-5 w-5 shrink-0" />
              Dashboard
            </button>
          ) : null}

          {HEADER_CONFIG.navigation.map((item) => (
            <div key={item.label}>
              {item.children ? (
                <>
                  <p className="mb-2 text-lg font-bold">{item.label}</p>
                  <div className="ml-4 flex flex-col gap-2">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={onClose}
                        className={`text-base font-medium ${getThemeClass(navClasses.menuItemColor || navClasses.linkColor, "text-theme-text")}`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={item.path!}
                  onClick={onClose}
                  className={`text-lg font-semibold ${getThemeClass(navClasses.linkColor, "text-theme-text")}`}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}

          <a
            href={`tel:${HEADER_CONFIG.phone.value}`}
            className="mt-4 flex items-center gap-2 font-semibold"
          >
            <Phone className="h-5 w-5" />
            {HEADER_CONFIG.phone.label}
          </a>
        </div>
      </div>
    </div>
  );
}
